/**
 * Admin editor for the watermark presets offered on the Watermarking page.
 *
 * Kept as its own component rather than inlined into Settings.js — that file is
 * already ~7,600 lines, and the whole preset list is one self-contained
 * app_config document with a single PUT.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Save } from "lucide-react";

import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const AX = { withCredentials: true };

const LAYER_LABELS = {
  tiled_text: "Repeating text",
  diagonal_band: "Diagonal band",
  corner_logo: "Corner logo",
  bottom_banner: "Bottom banner",
};

const BLANK = {
  tiled_text: {
    enabled: true, text: "NOT FOR MEDIA USE", opacity: 0.18, angle: -30,
    size_pct: 4.5, spacing_pct: 45, color: "#FFFFFF", stroke: true,
  },
  diagonal_band: {
    enabled: false, text: "INTERNAL USE ONLY", opacity: 0.3, angle: -30,
    size_pct: 8, color: "#FFFFFF", stroke: true,
  },
  corner_logo: {
    enabled: true, corner: "bottom-right", scale_pct: 15,
    opacity: 0.85, padding_pct: 2,
  },
  bottom_banner: {
    enabled: false, text: "{filename} — {date}", opacity: 0.55,
    height_pct: 8, text_color: "#FFFFFF", bg_color: "#000000",
  },
};

export default function WatermarkPresetsCard() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/app-config/watermark-presets`, AX)
      .then((r) => setPresets(r.data?.presets || []))
      .catch(() => toast.error("Couldn't load watermark presets"))
      .finally(() => setLoading(false));
  }, []);

  const patch = (index, updater) => {
    setPresets((prev) =>
      prev.map((p, i) => (i === index ? updater(p) : p))
    );
    setDirty(true);
  };

  const addPreset = () => {
    setPresets((prev) => [
      ...prev,
      {
        id: `wm_${Date.now().toString(36)}`,
        name: "New preset",
        layers: JSON.parse(JSON.stringify(BLANK)),
      },
    ]);
    setDirty(true);
  };

  const removePreset = (index) => {
    setPresets((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const save = async () => {
    if (presets.some((p) => !p.name?.trim())) {
      toast.error("Every preset needs a name");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/app-config/watermark-presets`, { presets }, AX
      );
      setPresets(res.data.presets || presets);
      setDirty(false);
      toast.success("Watermark presets saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save presets");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="card-dark mt-4">
      <CardHeader>
        <CardTitle>Watermark Presets</CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          The starting points offered on the Watermarking page. Text supports{" "}
          <code className="text-slate-300">{"{username}"}</code>,{" "}
          <code className="text-slate-300">{"{filename}"}</code>,{" "}
          <code className="text-slate-300">{"{date}"}</code> and{" "}
          <code className="text-slate-300">{"{time}"}</code>, filled in at export.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" />
          </div>
        ) : (
          <div className="space-y-3">
            {presets.length === 0 && (
              <p className="text-slate-500 text-sm text-center py-4">
                No presets configured
              </p>
            )}

            {presets.map((preset, index) => (
              <div
                key={preset.id || index}
                className="border border-slate-800 rounded-lg p-3 bg-slate-950/40"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Input
                    value={preset.name || ""}
                    onChange={(e) =>
                      patch(index, (p) => ({ ...p, name: e.target.value }))
                    }
                    placeholder="Preset name"
                    className="h-8 flex-1"
                  />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this preset?</AlertDialogTitle>
                        <AlertDialogDescription>
                          "{preset.name}" will be removed from the Watermarking
                          page. Files already watermarked with it aren't affected.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => removePreset(index)}
                          className="btn-destructive"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.keys(LAYER_LABELS).map((key) => {
                    const layer = preset.layers?.[key] || {};
                    return (
                      <div
                        key={key}
                        className="border border-slate-800 rounded p-2 space-y-2"
                      >
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={!!layer.enabled}
                            onCheckedChange={(v) =>
                              patch(index, (p) => ({
                                ...p,
                                layers: {
                                  ...p.layers,
                                  [key]: { ...BLANK[key], ...layer, enabled: !!v },
                                },
                              }))
                            }
                          />
                          <span className="text-xs text-slate-300">
                            {LAYER_LABELS[key]}
                          </span>
                        </label>
                        {layer.enabled && "text" in { ...BLANK[key] } && (
                          <Input
                            value={layer.text ?? BLANK[key].text}
                            onChange={(e) =>
                              patch(index, (p) => ({
                                ...p,
                                layers: {
                                  ...p.layers,
                                  [key]: { ...BLANK[key], ...layer, text: e.target.value },
                                },
                              }))
                            }
                            placeholder="Text"
                            className="h-7 text-xs"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  Opacity, size, angle and position are fine-tuned per export on
                  the Watermarking page.
                </p>
              </div>
            ))}

            <div className="flex items-center gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={addPreset}>
                <Plus className="w-4 h-4 mr-1" /> Add preset
              </Button>
              <div className="flex-1" />
              {dirty && (
                <span className="text-[11px] text-amber-400">Unsaved changes</span>
              )}
              <Button size="sm" onClick={save} disabled={saving || !dirty}>
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
